import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import SelectBase from '@/components/core/form/SelectBase';
import { numberFormat } from '@/utils/unitUtils';
import { useDerivedParam } from '@/stores/derivedCacheStore';
import { useDerivedValueFetch } from '@/hooks/useDerivedValueFetch';
import { isNumber } from '@/utils/miscUtils';
import { useCaseNodeParams, useNodeParams } from '@/stores/nodeStore';
import InputField from './InputField';
import Tooltip from '@/components/core/Tooltip';
import { DISPLAY_TYPES } from '@/consts';

const setValueAs = v => (v === '' || v === null || v === undefined ? v : isNumber(v) ? parseFloat(v) : v);

const toNumericDisplay = result => {
  if (result == null) return null;
  const n = typeof result === 'object' && 'scalar' in result ? result.scalar : result;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

const LocationScalingTable = ({ fullName, param, paramSchema, onChange, label, tooltip, source }) => {
  const { setValue, register } = useFormContext();
  const caseParams = useCaseNodeParams();
  const nodeParams = useNodeParams();
  const parentName = fullName.split('.')[0];
  const prefix = `${fullName}.`;
  const modelLocationDisplay = caseParams?.location?.primary?.value?.address ?? '—';

  const parentSchema = nodeParams?.find(s => s.name === parentName);
  const baseValueSchema = parentSchema?.params?.find(s => s.name === 'base_value');
  const baseValueParam = caseParams?.[parentName]?.params?.base_value;
  const baseValueName = `${parentName}.base_value`;

  const baseLocationSchema = paramSchema?.params?.find(p => p.name === 'base_location');
  const baseLocationParam = param?.params?.base_location;
  const baseLocationName = `${prefix}base_location`;

  const locationFactorName = `${prefix}location_factor`;
  const effectiveValueName = `${prefix}effective_value`;
  const locationFactorResult = useDerivedParam(locationFactorName);
  const effectiveValueResult = useDerivedParam(effectiveValueName);
  const locationFactorMeta = param?.params?.location_factor?.primary?.value ?? {};
  const effectiveValueMeta = param?.params?.effective_value?.primary?.value ?? {};

  useDerivedValueFetch({
    name: locationFactorName,
    shouldFetch: locationFactorMeta?.fetch || !locationFactorResult,
    changedDependencies: locationFactorMeta?.changed_dependencies,
  });
  useDerivedValueFetch({
    name: effectiveValueName,
    shouldFetch: effectiveValueMeta?.fetch || !effectiveValueResult,
    changedDependencies: effectiveValueMeta?.changed_dependencies,
  });

  const locationFactorNum = toNumericDisplay(locationFactorResult);
  const effectiveValueNum = toNumericDisplay(effectiveValueResult);

  const registration = register(baseLocationName, {
    setValueAs,
    onChange: e => {
      const convertedValue = setValueAs(e.target.value);
      setValue(baseLocationName, convertedValue, { shouldValidate: true, shouldDirty: true });
      if (onChange) onChange(e);
    },
  });

  return (
    <table className="w-full">
      <thead className="text-xs border-b">
        <tr>
          <td className="p-2 pl-4 w-1/4 font-medium">
            {label && (
              <span className="flex items-center gap-1">
                {label}
                {tooltip && <Tooltip label={label} text={tooltip} source={source} />}
              </span>
            )}
          </td>
          <td className="p-2 w-2/5 font-medium">Reference Location</td>
          <td className="p-2 pr-4 w-2/5 font-medium">Model Location</td>
        </tr>
      </thead>
      <tbody className="text-sm">
        <tr className="border-b border-gray-100">
          <td className="p-2 pl-4 font-medium">Location</td>
          <td className="p-2">
            <SelectBase
              {...registration}
              allowNull={baseLocationSchema?.allow_null}
              value={baseLocationParam?.primary?.value}
              options={baseLocationParam?.options ?? []}
              disabled={!baseLocationSchema?.editable}
            />
          </td>
          <td className="p-2 text-gray-600">{modelLocationDisplay}</td>
        </tr>
        <tr className="border-b border-gray-100">
          <td className="p-2 pl-4 font-medium">Factor</td>
          <td className="p-2 text-gray-600">—</td>
          <td className="p-2 text-gray-600">{locationFactorNum != null ? numberFormat(locationFactorNum, 4) : '—'}</td>
        </tr>
        <tr>
          <td className="p-2 pl-4 font-medium">Cost</td>
          <td className="p-2">
            {baseValueSchema && baseValueParam ? (
              <InputField
                name={baseValueName}
                parentName={parentName}
                paramSchema={baseValueSchema}
                param={baseValueParam}
                displayType={DISPLAY_TYPES.inline}
              />
            ) : (
              '—'
            )}
          </td>
          <td className="p-2 text-gray-600">{effectiveValueNum != null ? numberFormat(effectiveValueNum, 2) : '—'}</td>
        </tr>
      </tbody>
    </table>
  );
};

LocationScalingTable.propTypes = {
  fullName: PropTypes.string.isRequired,
  param: PropTypes.object.isRequired,
  paramSchema: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  label: PropTypes.string,
  tooltip: PropTypes.string,
  source: PropTypes.string,
};

export default LocationScalingTable;
